// Lib
import { createSelector } from 'reselect';

// Utils
import { hasNotChanged } from '../../utils/react/propsComparisons';
import { createCustomEqualDeepSelector } from '../../utils/milanoteReselect/milanoteReselect';
import { getBreadcrumb } from './breadcrumbUtil';

// Selectors
import { boardResourcesSelector } from '../../element/board/boardSelector';
import { getDeepChildrenModCache } from '../../element/cache/deepChildrenModCacheSelector';
import { getCurrentUserRootBoardId, isGuestSelector } from '../../user/currentUserSelector';
import { getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { getElements } from '../../element/selectors/elementsSelector';
import { areAliasesInitialised } from '../../app/initialisation/initialisationSelector';
import { getAclIdsSelector } from '../../utils/permissions/permissionsSelector';

// 2nd argument
const currentBoardIdHasNotChanged = hasNotChanged(1);
// 4th argument
const aclIdsHasNotChanged = hasNotChanged(3);
// 5th argument
const deepChildrenModificationTimeHasNotChanged = hasNotChanged(4);
// 6th argument
const boardResourcesHasNotChanged = hasNotChanged(5);

/**
 * Do not recalculate, unless:
 * - The deep children modified time has changed (this happens when elements change parents)
 * - ACL IDs change (e.g. new tokens are received)
 * - Current board ID changes (navigate to new board)
 * - Board resource changes (new boards are received)
 */
const breadcrumbSelectorEqualityChecker = (prevArgs, nextArgs) =>
    // Deep children mod cache
    deepChildrenModificationTimeHasNotChanged(prevArgs, nextArgs) &&
    // ACL IDs haven't changed
    aclIdsHasNotChanged(prevArgs, nextArgs) &&
    // Current board ID
    currentBoardIdHasNotChanged(prevArgs, nextArgs) &&
    // Board resources
    boardResourcesHasNotChanged(prevArgs, nextArgs);

const createCustomElementChildrenSelector = createCustomEqualDeepSelector(breadcrumbSelectorEqualityChecker);

// FIXME-PERF Potentially improve this
// NOTE: The order of the functions is important for the equality checker above
export const breadcrumbSelector = createCustomElementChildrenSelector(
    getElements,
    getCurrentBoardId,
    getCurrentUserRootBoardId,
    getAclIdsSelector,
    // The following two are simply used in the equality check, so they are not needed when building the breadcrumb
    getDeepChildrenModCache,
    boardResourcesSelector,
    (elements, currentBoardId, rootBoardId, aclIds) => getBreadcrumb({ elements, currentBoardId, rootBoardId, aclIds }),
);

/**
 * A detached breadcrumb is only valid if the current user is a guest or the current user has all their
 * aliases loaded.
 */
export const detachedBreadcrumbIsValidSelector = createSelector(
    areAliasesInitialised,
    isGuestSelector,
    (aliasesAreInitialised, isGuest) => isGuest || aliasesAreInitialised,
);
